import type {ReactElement} from 'react';
import {useCallback, useRef} from 'react';
import {useLatest} from 'ahooks';
import styles from './chat-history-search.module.scss';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {useAiChatSize} from '@AiChat/hooks/useAiChatSize';
import {InputControlItemType, InputFieldSize, InputFieldType} from '@Components/input-field-v2/input-field.types';
import {InputField} from '@Components/input-field-v2';
import {HISTORY_FETCH_LIMIT} from '@/ai-chat/components/chat-history-list/chat-history-list.types';
import useBackgroundClick from '@Hooks/useBackgroundClick';

export function ChatHistorySearch(): ReactElement {
  const {state, actions} = useAiChat();
  const {isSmall} = useAiChatSize();
  const searchAreaRef = useRef<HTMLDivElement>(null);
  const searchQueryRef = useLatest(state.searchQuery);

  const closeIfEmpty = useCallback((): void => {
    if (!searchQueryRef.current?.trim()) {
      actions.setSearchQuery(undefined);
      actions.closeChatHistorySearch();
    }
  }, [actions]);

  useBackgroundClick(searchAreaRef, closeIfEmpty, true);

  const onSearchInput = (input: string): void => {
    if (input === '') {
      actions.setSearchQuery(undefined);
    } else {
      actions.setSearchQuery(input);
    }
  };

  const onSearchInputChangeEnded = (value: string): void => {
    const query = value.trim() === '' ? undefined : value.trim();

    actions.setSearchQuery(query);
    void actions.fetchChatHistory(query, 0, HISTORY_FETCH_LIMIT);
  };

  const onClearSearch = (): void => {
    actions.setSearchQuery(undefined);

    void actions.fetchChatHistory(undefined, 0, HISTORY_FETCH_LIMIT);
  };

  const getSearchBar = (): ReactElement => {
    return (
      <div ref={searchAreaRef} className={styles.searchBox}>
        <InputField
          autoFocus={!isSmall}
          input={state.searchQuery}
          onInputChange={onSearchInput}
          onInputChangeEnded={onSearchInputChangeEnded}
          placeholder={window.i18next.t('pmwjs_search_chats')}
          inputType={InputFieldType.SEARCH}
          size={InputFieldSize.XSMALL}
          inputClassName={styles.searchBoxBorder}
          controls={{
            showDefaultControls: false,
            showSingleDefaultControlOnly: false,
            className: styles.searchBoxIcon,
            customControls: [
              {
                type: InputControlItemType.ICONS_ONLY,
                id: 'search-icon',
                icon: state.searchQuery !== undefined ? 'icon-close' : 'icon-search',
              },
            ],
            onClick: onClearSearch,
          }}
        />
      </div>
    );
  };

  return getSearchBar();
}
